import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Package, ArrowLeft, FileText } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumb from '../components/navigation/Breadcrumb';
import ZoomableImage from '../components/ui/ZoomableImage';
import ShareButtons from '../components/ui/ShareButtons';
import productsData from '../data/completeProductsData';
import { trackPageView } from '../utils/analytics';

// camelCase / snake_case spec keys -> readable labels
const formatSpecKey = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const ProductPage = () => {
  const { categorySlug, productSlug } = useParams();
  
  useEffect(() => {
    trackPageView(`Product: ${productSlug}`);
  }, [productSlug]);

  // Find product data
  const getProductData = () => {
    const categories = [
      productsData.laryngoStroboscopyProcessors,
      productsData.surgeryProcessors,
      productsData.officeMobileProcessors,
      productsData.cameraHeads,
      productsData.flexibleEndoscopes,
      productsData.fibreOpticEndoscopes,
      productsData.compactCameras,
      productsData.rigidEndoscopes,
      productsData.illuminationSystems,
      productsData.documentationSystems,
      productsData.medicalMonitors,
      productsData.softwareSystems
    ];
    
    const category = categories.find(cat => cat.slug === categorySlug);
    if (!category) return { product: null, category: null };
    
    const product = category.products.find(p => p.slug === productSlug);
    return { product, category };
  };

  const { product, category } = getProductData();

  if (!product || !category) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-black pt-32 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Product Not Found</h1>
            <Link to="/products" className="btn-primary">
              Back to Products
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${product.name} - XION Medical Philippines`}
        description={product.longDescription || product.description}
        path={`/products/${categorySlug}/${productSlug}`}
      />
      <Header />
      <main className="min-h-screen bg-hud pt-32 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb 
            items={[
              { label: 'Products', link: '/products' },
              { label: category.name, link: `/products/${categorySlug}` },
              { label: product.name }
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Left: Product Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:sticky lg:top-32 h-fit"
            >
              <div className="aspect-square bg-gradient-to-br from-[#00FFD1]/10 to-transparent border border-[#00FFD1]/30 rounded-lg flex items-center justify-center overflow-hidden p-8">
                {product.images && product.images[0] && !product.images[0].startsWith('/images/') ? (
                  <img 
                    src={product.images[0]} 
                    alt={product.name}
                    className="w-full h-full object-contain product-float drop-shadow-[0_20px_45px_rgba(0,0,0,0.5)]"
                    data-testid="product-detail-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<svg class="text-[#00FFD1]/40" width="120" height="120" fill="currentColor"><rect width="120" height="120" rx="8"/></svg>';
                    }}
                  />
                ) : (
                  <Package size={120} className="text-[#00FFD1]/40" />
                )}
              </div>
            </motion.div>

            {/* Right: Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="inline-block px-4 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full mb-4">
                <span className="text-[#00FFD1] text-sm font-mono">{category.category}</span>
              </div>
              
              <h1 className="text-5xl font-bold text-white mb-4">{product.name}</h1>
              <p className="text-xl text-[#00FFD1] mb-6">{product.tagline}</p>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                {product.description}
              </p>

              {/* Overview (long description) */}
              {product.longDescription && (
                <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6 mb-8" data-testid="product-overview">
                  <h2 className="text-2xl font-bold text-white mb-3">Overview</h2>
                  <p className="text-white/70 leading-relaxed">{product.longDescription}</p>
                </div>
              )}

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6 mb-8">
                  <h2 className="text-2xl font-bold text-white mb-4">Key Features</h2>
                  <ul className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check size={20} className="text-[#00FFD1] mt-0.5 flex-shrink-0" />
                        <span className="text-white/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Software / system modules */}
              {product.modules && product.modules.length > 0 && (
                <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6 mb-8" data-testid="product-modules">
                  <h2 className="text-2xl font-bold text-white mb-4">Modules</h2>
                  <div className="grid md:grid-cols-2 gap-5">
                    {product.modules.map((m, idx) => (
                      <div key={idx} className="bg-black/30 border border-white/10 rounded-lg overflow-hidden flex flex-col" data-testid={`product-module-${idx}`}>
                        {m.image && (
                          <div className="bg-gradient-to-b from-white/[0.05] to-transparent p-2 border-b border-white/10">
                            <ZoomableImage
                              src={m.image}
                              alt={`${m.name} — DiVAS module`}
                              className="w-full h-auto rounded"
                              testId={`product-module-image-${idx}`}
                            />
                          </div>
                        )}
                        <div className="p-4 border-l-2 border-[#00FFD1]/40 flex-1">
                          <h3 className="text-[#00FFD1] font-semibold mb-1">{m.name}</h3>
                          <p className="text-white/70 text-sm leading-relaxed">{m.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Technical Specifications */}
              <div className="bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden mb-6" data-testid="product-specs">
                <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-white/10 bg-white/[0.02]">
                  <h2 className="text-xl font-bold text-white tracking-tight">Technical Specifications</h2>
                  <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">XION source data</span>
                </div>

                {product.specifications ? (
                  <dl className="divide-y divide-white/5">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div
                        key={key}
                        className="grid grid-cols-1 sm:grid-cols-[minmax(0,190px)_1fr] gap-0.5 sm:gap-4 px-6 py-3 hover:bg-white/[0.02] transition-colors"
                      >
                        <dt className="text-[11px] font-bold uppercase tracking-wider text-[#00FFD1]/90 font-mono pt-0.5">
                          {formatSpecKey(key)}
                        </dt>
                        <dd className="text-white font-medium text-[15px] leading-snug">
                          {Array.isArray(value) ? value.join(' · ') : value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="px-6 py-4 text-white/50 text-sm">
                    Full datasheet specifications available on request — contact FMSInc. for the technical brochure.
                  </p>
                )}

              </div>

              {/* High-visibility brochure / quotation CTA */}
              <Link
                to="/contact-ph"
                state={{ productName: product.name }}
                data-testid="request-brochure-cta"
                className="group flex items-center justify-between gap-4 w-full bg-gradient-to-r from-[#00FFD1] to-[#00B4D8] text-black font-bold rounded-lg px-6 py-4 mb-6 hover:shadow-[0_12px_40px_-8px_rgba(0,255,209,0.55)] transition-shadow"
              >
                <span className="flex items-center gap-3">
                  <FileText size={20} />
                  <span className="text-base">Request Technical Brochure / Quotation</span>
                </span>
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>

              {/* Distributor CTA */}
              <div className="bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-2 border-[#00FFD1]/50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  Available in the Philippines
                </h3>
                <p className="text-white/70 mb-4 text-sm">
                  {productsData.distributor.statement}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/contact-ph" state={{ productName: product.name }} className="btn-primary flex-1 justify-center">
                    Request Quote
                    <ArrowRight size={16} />
                  </Link>
                  <Link to={`/products/${categorySlug}`} className="btn-secondary flex-1 justify-center">
                    <ArrowLeft size={16} />
                    Back to {category.name}
                  </Link>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <ShareButtons title={`${product.name} — XION Medical Philippines`} testId="product-share" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ProductPage;
